/**
 * B4 status-effect auras — three persistent ShaderMaterial overlays
 * that hang around the chassis when a MechState status flag is set.
 *
 *   <BurningAura active />  — scrolling noise UV + orange-red additive.
 *   <FrozenAura active  />  — cyan Fresnel rim + crackle desat.
 *   <EmpAura active     />  — pulsing blue-white quantized pattern +
 *                             radial arc lines (drei `<LightningStrike>`-
 *                             style, but kept simple with shader).
 *
 * Each is mounted as a *cylinder* enclosing the chassis (radius 0.5,
 * height 1.4) with a custom ShaderMaterial. The shaders are simple
 * enough that we author them inline rather than reaching for new
 * deps. All use `transparent` + additive blending so they layer over
 * the voxel chassis without obscuring it.
 *
 * Caller is responsible for the engine-flag gating — these only
 * read an `active: boolean` prop and skip rendering when false.
 */

import { useEffect, useMemo, useRef } from "react";
import {
  AdditiveBlending,
  type Color as ColorT,
  Color,
  type Mesh,
  ShaderMaterial,
  Vector3,
} from "three";
import { useFrame } from "@react-three/fiber";

// Shared cylinder dimensions so the three auras stack cleanly.
const AURA_RADIUS = 0.42;
const AURA_HEIGHT = 1.35;
const AURA_LOCAL_Y = 0.65; // centered on the chassis mid-height

// ── Burning aura ──────────────────────────────────────────────────────

const BURNING_VERTEX = /* glsl */ `
varying vec3 vWorldPos;
varying vec3 vNormal;
varying vec2 vUv;
void main() {
  vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
  vNormal = normalize(normalMatrix * normal);
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const BURNING_FRAGMENT = /* glsl */ `
uniform float uTime;
uniform vec3 uColorWarm;
uniform vec3 uColorHot;
varying vec3 vNormal;
varying vec2 vUv;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i + vec2(0,0)), hash(i + vec2(1,0)), u.x),
             mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), u.x), u.y);
}

void main() {
  // Scrolling UV makes the noise climb up the chassis like flame.
  vec2 uv = vUv * vec2(3.0, 4.0) + vec2(0.0, -uTime * 0.6);
  float n = noise(uv) * 0.6 + noise(uv * 2.0 + uTime * 0.8) * 0.4;
  // Stronger near the bottom, fades upward.
  float vertical = smoothstep(1.0, 0.05, vUv.y);
  float intensity = n * vertical;
  // Hot near the bottom (white-hot), warm toward the top (orange-red).
  vec3 col = mix(uColorWarm, uColorHot, smoothstep(0.3, 0.95, intensity));
  // Edge-on fragments brighter — chassis silhouette glow.
  float rim = pow(1.0 - max(0.0, dot(vNormal, vec3(0.0, 0.0, 1.0))), 2.0);
  float alpha = intensity * (0.5 + rim * 0.6);
  if (alpha < 0.04) discard;
  gl_FragColor = vec4(col * intensity * 1.6, alpha);
}
`;

interface AuraProps {
  active: boolean;
}

export function BurningAura({ active }: AuraProps) {
  const ref = useRef<Mesh>(null);
  const material = useMemo(
    () =>
      new ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uColorWarm: { value: new Color("#ff5a1f") },
          uColorHot: { value: new Color("#fff0c4") },
        },
        vertexShader: BURNING_VERTEX,
        fragmentShader: BURNING_FRAGMENT,
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
      }),
    [],
  );
  useEffect(() => () => material.dispose(), [material]);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.visible = active;
    if (!active) return;
    material.uniforms.uTime.value = clock.elapsedTime;
  });
  return (
    <mesh
      ref={ref}
      position={[0, AURA_LOCAL_Y, 0]}
      material={material}
      visible={false}
    >
      <cylinderGeometry args={[AURA_RADIUS, AURA_RADIUS, AURA_HEIGHT, 16, 1, true]} />
    </mesh>
  );
}

// ── Frozen aura ───────────────────────────────────────────────────────

const FROZEN_VERTEX = /* glsl */ `
varying vec3 vNormal;
varying vec2 vUv;
varying vec3 vViewPos;
void main() {
  vNormal = normalize(normalMatrix * normal);
  vUv = uv;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vViewPos = mv.xyz;
  gl_Position = projectionMatrix * mv;
}
`;

const FROZEN_FRAGMENT = /* glsl */ `
uniform float uTime;
uniform vec3 uColor;
varying vec3 vNormal;
varying vec2 vUv;
varying vec3 vViewPos;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

void main() {
  // Fresnel rim — bright at grazing angles, dark face-on.
  vec3 viewDir = normalize(-vViewPos);
  float fres = pow(1.0 - max(0.0, dot(vNormal, viewDir)), 2.5);
  // Crackle pattern — quantized hash grid.
  vec2 g = floor(vUv * vec2(14.0, 18.0));
  float crackle = hash(g);
  float frost = step(0.68, crackle) * 0.55;
  vec3 col = uColor * (fres * 1.6 + frost * 0.5);
  float alpha = fres * 0.9 + frost * 0.25;
  if (alpha < 0.05) discard;
  // Gentle pulse so the aura breathes.
  alpha *= 0.7 + 0.3 * sin(uTime * 1.2);
  gl_FragColor = vec4(col, alpha);
}
`;

export function FrozenAura({ active }: AuraProps) {
  const ref = useRef<Mesh>(null);
  const material = useMemo(
    () =>
      new ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uColor: { value: new Color("#a5d8ff") },
        },
        vertexShader: FROZEN_VERTEX,
        fragmentShader: FROZEN_FRAGMENT,
        transparent: true,
        depthWrite: false,
      }),
    [],
  );
  useEffect(() => () => material.dispose(), [material]);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.visible = active;
    if (!active) return;
    material.uniforms.uTime.value = clock.elapsedTime;
  });
  return (
    <mesh
      ref={ref}
      position={[0, AURA_LOCAL_Y, 0]}
      material={material}
      visible={false}
    >
      <cylinderGeometry args={[AURA_RADIUS, AURA_RADIUS, AURA_HEIGHT, 16, 1, true]} />
    </mesh>
  );
}

// ── EMP / ECM bubble aura ─────────────────────────────────────────────

const EMP_VERTEX = /* glsl */ `
varying vec3 vNormal;
varying vec3 vWorldPos;
varying vec2 vUv;
void main() {
  vNormal = normalize(normalMatrix * normal);
  vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const EMP_FRAGMENT = /* glsl */ `
uniform float uTime;
uniform vec3 uColor;
varying vec3 vNormal;
varying vec3 vWorldPos;
varying vec2 vUv;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

void main() {
  // Vertical scan-pulse — bands traveling up the bubble.
  float scan = sin((vUv.y * 18.0) - uTime * 3.2);
  float band = smoothstep(0.4, 1.0, scan);
  // Quantized hex-like grid for the "EM field" texture.
  vec2 g = floor(vUv * vec2(10.0, 14.0));
  float cell = step(0.5, hash(g + floor(uTime * 1.5)));
  // Hemisphere top brighter than bottom (we're inside a sphere).
  float verticalK = smoothstep(0.0, 1.0, vUv.y);
  vec3 col = uColor * (band * 1.4 + cell * 0.5 + verticalK * 0.3);
  float alpha = band * 0.8 + cell * 0.3;
  alpha *= 0.55; // overall translucency
  if (alpha < 0.05) discard;
  gl_FragColor = vec4(col, alpha);
}
`;

export function EmpAura({ active }: AuraProps) {
  const ref = useRef<Mesh>(null);
  const material = useMemo(
    () =>
      new ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uColor: { value: new Color("#9ec5ff") },
        },
        vertexShader: EMP_VERTEX,
        fragmentShader: EMP_FRAGMENT,
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
      }),
    [],
  );
  useEffect(() => () => material.dispose(), [material]);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.visible = active;
    if (!active) return;
    material.uniforms.uTime.value = clock.elapsedTime;
  });
  return (
    <mesh
      ref={ref}
      position={[0, AURA_LOCAL_Y, 0]}
      material={material}
      visible={false}
    >
      {/* Hemisphere (cap on top, open bottom) so the player sees the
          field as a half-dome over the chassis rather than a closed
          can. We extrude the cylinder a bit and let the additive
          blending sell it. */}
      <cylinderGeometry args={[AURA_RADIUS * 1.08, AURA_RADIUS * 1.08, AURA_HEIGHT * 1.05, 18, 1, true]} />
    </mesh>
  );
}

/** Tiny anchor type used by the parent to drive multi-aura layout. */
export const AURA_LOCAL_VEC = new Vector3(0, AURA_LOCAL_Y, 0);
export type AuraColor = ColorT;
